import React, { useState, useEffect, useCallback } from "react";
import {
  Table,
  Tabs,
  Button,
  Input,
  Select,
  Switch,
  Modal,
  Form,
  message,
} from "antd";
import { PlusOutlined, EditOutlined, DeleteOutlined } from "@ant-design/icons";
import type { ColumnsType } from "antd/es/table";
import { NumberInput } from "../components/number_input";
import { addOnServicesApi, AddOnServiceDto, CreateAddOnServiceDto } from "~/services/add-on-services";

interface AddOnServicesTabProps {
  addOnServices?: AddOnServiceDto[];
  onAddOnServicesChange?: (services: AddOnServiceDto[]) => void;
}

export const AddOnServicesTab: React.FC<AddOnServicesTabProps> = ({
  addOnServices: propServices,
  onAddOnServicesChange,
}) => {
  const [form] = Form.useForm();
  const [isModalVisible, setIsModalVisible] = useState(false);
  const [editingService, setEditingService] = useState<AddOnServiceDto | null>(null);
  const [activeTab, setActiveTab] = useState<"WAREHOUSE" | "FABRICATION" | "GENERAL">("WAREHOUSE");
  const [services, setServices] = useState<AddOnServiceDto[]>([]);
  const [loading, setLoading] = useState(false);

  const loadServices = useCallback(async () => {
    try {
      setLoading(true);
      const data = await addOnServicesApi.getAll();
      setServices(data);
      onAddOnServicesChange?.(data);
    } catch (error) {
      console.error('Failed to load services:', error);
      message.error("Failed to load services");
    } finally {
      setLoading(false);
    }
  }, [onAddOnServicesChange]);

  useEffect(() => {
    if (propServices) {
      setServices(propServices);
    } else {
      loadServices();
    }
  }, [propServices, loadServices]);

  const handleServiceChange = async (
    id: string,
    field: keyof AddOnServiceDto,
    value: any
  ) => {
    try {
      const updatedService = await addOnServicesApi.update(id, { [field]: value });
      const updatedServices = services.map((service) =>
        service.id === id ? updatedService : service
      );
      setServices(updatedServices);
      onAddOnServicesChange?.(updatedServices);
    } catch (error) {
      console.error('Failed to update service:', error);
      message.error("Failed to update service");
    }
  };

  const handleAddService = () => {
    setEditingService(null);
    form.resetFields();
    form.setFieldsValue({ type: activeTab, isActive: true });
    setIsModalVisible(true);
  };

  const handleEditService = (service: AddOnServiceDto) => {
    setEditingService(service);
    form.setFieldsValue(service);
    setIsModalVisible(true);
  };

  const handleDeleteService = async (id: string) => {
    try {
      await addOnServicesApi.delete(id);
      const updatedServices = services.filter((service) => service.id !== id);
      setServices(updatedServices);
      onAddOnServicesChange?.(updatedServices);
      message.success("Service deleted successfully");
    } catch (error) {
      console.error('Failed to delete service:', error);
      message.error("Failed to delete service");
    }
  };

  const handleModalOk = async () => {
    try {
      const values = await form.validateFields();
      
      if (editingService) {
        // Update existing service
        const updatedService = await addOnServicesApi.update(editingService.id, values);
        const updatedServices = services.map((service) =>
          service.id === editingService.id ? updatedService : service
        );
        setServices(updatedServices);
        onAddOnServicesChange?.(updatedServices);
        message.success("Service updated successfully");
      } else {
        // Add new service
        const createData: CreateAddOnServiceDto = {
          ...values,
          serviceRef: `${values.type}-${Date.now()}`,
        };
        const newService = await addOnServicesApi.create(createData);
        const updatedServices = [...services, newService];
        setServices(updatedServices);
        onAddOnServicesChange?.(updatedServices);
        message.success("Service created successfully");
      }
      setIsModalVisible(false);
    } catch (error) {
      console.error('Failed to save service:', error);
      message.error("Failed to save service");
    }
  };

  const warehouseColumns: ColumnsType<AddOnServiceDto> = [
    {
      title: "Service Name",
      dataIndex: "name",
      key: "name",
    },
    {
      title: "Rate",
      dataIndex: "rate",
      key: "rate",
      render: (value: number, record: AddOnServiceDto) => (
        <NumberInput
          value={value || 0}
          prefix="$"
          onChange={(newValue) =>
            handleServiceChange(record.id, "rate", newValue)
          }
        />
      ),
    },
    {
      title: "Unit",
      dataIndex: "unit",
      key: "unit",
      render: (value: AddOnServiceDto["unit"], record: AddOnServiceDto) => (
        <Select
          value={value}
          onChange={(newValue) =>
            handleServiceChange(record.id, "unit", newValue)
          }
          className="w-full"
        >
          <Select.Option value="CU_FT">Cu Foot</Select.Option>
          <Select.Option value="HOURLY">Hourly</Select.Option>
          <Select.Option value="EACH">Each</Select.Option>
        </Select>
      ),
    },
    {
      title: "Minimum Fee",
      dataIndex: "minimumFee",
      key: "minimumFee",
      render: (value: number, record: AddOnServiceDto) => (
        <NumberInput
          value={value || 0}
          prefix="$"
          onChange={(newValue) =>
            handleServiceChange(record.id, "minimumFee", newValue)
          }
        />
      ),
    },
    {
      title: "Active",
      dataIndex: "isActive",
      key: "isActive",
      render: (value: boolean, record: AddOnServiceDto) => (
        <Switch
          checked={value}
          onChange={(checked) =>
            handleServiceChange(record.id, "isActive", checked)
          }
        />
      ),
    },
    {
      title: "Actions",
      key: "actions",
      render: (_, record: AddOnServiceDto) => (
        <div className="flex gap-2">
          <Button
            icon={<EditOutlined />}
            size="small"
            onClick={() => handleEditService(record)}
          />
          <Button
            icon={<DeleteOutlined />}
            size="small"
            danger
            onClick={() => handleDeleteService(record.id)}
          />
        </div>
      ),
    },
  ];

  const fabricationColumns: ColumnsType<AddOnServiceDto> = [
    {
      title: "Service Name",
      dataIndex: "name",
      key: "name",
    },
    {
      title: "Rate Type",
      dataIndex: "fabricationRateType",
      key: "fabricationRateType",
      render: (
        value: AddOnServiceDto["fabricationRateType"],
        record: AddOnServiceDto
      ) => (
        <Select
          value={value}
          onChange={(newValue) =>
            handleServiceChange(record.id, "fabricationRateType", newValue)
          }
          className="w-full"
        >
          <Select.Option value="SQ_FT">Per Square Foot</Select.Option>
          <Select.Option value="HOUR">Per Hour</Select.Option>
        </Select>
      ),
    },
    {
      title: "Rate",
      dataIndex: "rate",
      key: "rate",
      render: (value: number, record: AddOnServiceDto) => (
        <NumberInput
          value={value || 0}
          prefix="$"
          onChange={(newValue) =>
            handleServiceChange(record.id, "rate", newValue)
          }
        />
      ),
    },
    {
      title: "Minimum Charge",
      dataIndex: "minimumCharge",
      key: "minimumCharge",
      render: (value: number, record: AddOnServiceDto) => (
        <NumberInput
          value={value || 0}
          prefix="$"
          onChange={(newValue) =>
            handleServiceChange(record.id, "minimumCharge", newValue)
          }
        />
      ),
    },
    {
      title: "Active",
      dataIndex: "isActive",
      key: "isActive",
      render: (value: boolean, record: AddOnServiceDto) => (
        <Switch
          checked={value}
          onChange={(checked) =>
            handleServiceChange(record.id, "isActive", checked)
          }
        />
      ),
    },
    {
      title: "Actions",
      key: "actions",
      render: (_, record: AddOnServiceDto) => (
        <div className="flex gap-2">
          <Button
            icon={<EditOutlined />}
            size="small"
            onClick={() => handleEditService(record)}
          />
          <Button
            icon={<DeleteOutlined />}
            size="small"
            danger
            onClick={() => handleDeleteService(record.id)}
          />
        </div>
      ),
    },
  ];

  const generalColumns: ColumnsType<AddOnServiceDto> = [
    {
      title: "Service Name",
      dataIndex: "name",
      key: "name",
    },
    {
      title: "Description",
      dataIndex: "description",
      key: "description",
      render: (value: string, record: AddOnServiceDto) => (
        <Input.TextArea
          value={value}
          onChange={(e) =>
            handleServiceChange(record.id, "description", e.target.value)
          }
          rows={2}
        />
      ),
    },
    {
      title: "Active",
      dataIndex: "isActive",
      key: "isActive",
      render: (value: boolean, record: AddOnServiceDto) => (
        <Switch
          checked={value}
          onChange={(checked) =>
            handleServiceChange(record.id, "isActive", checked)
          }
        />
      ),
    },
    {
      title: "Actions",
      key: "actions",
      render: (_, record: AddOnServiceDto) => (
        <div className="flex gap-2">
          <Button
            icon={<EditOutlined />}
            size="small"
            onClick={() => handleEditService(record)}
          />
          <Button
            icon={<DeleteOutlined />}
            size="small"
            danger
            onClick={() => handleDeleteService(record.id)}
          />
        </div>
      ),
    },
  ];

  const getFilteredServices = (type: string) =>
    services.filter((service) => service.type === type);

  const tabItems = [
    {
      key: "WAREHOUSE",
      label: "Warehouse Services",
      children: (
        <div className="space-y-4">
          <div className="flex justify-between items-center">
            <div>
              <h4 className="text-md font-medium">Warehouse Add-on Services</h4>
              <p className="text-gray-500 text-sm">
                Services related to storage, handling, and warehouse operations
              </p>
            </div>
            <Button
              type="primary"
              icon={<PlusOutlined />}
              onClick={handleAddService}
            >
              Add Warehouse Service
            </Button>
          </div>
          <Table
            columns={warehouseColumns}
            dataSource={getFilteredServices("WAREHOUSE")}
            rowKey="id"
            pagination={false}
            className="bg-white"
            loading={loading}
          />
        </div>
      ),
    },
    {
      key: "FABRICATION",
      label: "Fabrication Services",
      children: (
        <div className="space-y-4">
          <div className="flex justify-between items-center">
            <div>
              <h4 className="text-md font-medium">
                Fabrication Add-on Services
              </h4>
              <p className="text-gray-500 text-sm">
                Services related to custom fabrication, framing, and
                conservation
              </p>
            </div>
            <Button
              type="primary"
              icon={<PlusOutlined />}
              onClick={handleAddService}
            >
              Add Fabrication Service
            </Button>
          </div>
          <Table
            columns={fabricationColumns}
            dataSource={getFilteredServices("FABRICATION")}
            rowKey="id"
            pagination={false}
            className="bg-white"
            loading={loading}
          />
        </div>
      ),
    },
    {
      key: "GENERAL",
      label: "General Services",
      children: (
        <div className="space-y-4">
          <div className="flex justify-between items-center">
            <div>
              <h4 className="text-md font-medium">General Add-on Services</h4>
              <p className="text-gray-500 text-sm">
                Additional services that can be added to any service ticket
              </p>
            </div>
            <Button
              type="primary"
              icon={<PlusOutlined />}
              onClick={handleAddService}
            >
              Add General Service
            </Button>
          </div>
          <Table
            columns={generalColumns}
            dataSource={getFilteredServices("GENERAL")}
            rowKey="id"
            pagination={false}
            className="bg-white"
            loading={loading}
          />
        </div>
      ),
    },
  ];

  return (
    <div className="space-y-6">
      <div>
        <h3 className="text-lg font-medium mb-2">Add-on Services</h3>
        <p className="text-gray-500 mb-4">
          Manage additional services that can be added to collection, delivery,
          and site service tickets
        </p>
      </div>

      <Tabs
        activeKey={activeTab}
        onChange={(key) => setActiveTab(key as any)}
        items={tabItems}
      />

      <Modal
        title={editingService ? "Edit Service" : "Add New Service"}
        open={isModalVisible}
        onOk={handleModalOk}
        onCancel={() => setIsModalVisible(false)}
        width={600}
      >
        <Form form={form} layout="vertical" className="space-y-4">
          <Form.Item
            name="name"
            label="Service Name"
            rules={[{ required: true, message: "Please enter service name" }]}
          >
            <Input placeholder="Enter service name" />
          </Form.Item>

          <Form.Item
            name="type"
            label="Service Type"
            rules={[{ required: true, message: "Please select service type" }]}
          >
            <Select disabled={!!editingService}>
              <Select.Option value="WAREHOUSE">Warehouse Service</Select.Option>
              <Select.Option value="FABRICATION">
                Fabrication Service
              </Select.Option>
              <Select.Option value="GENERAL">General Service</Select.Option>
            </Select>
          </Form.Item>

          <Form.Item
            noStyle
            shouldUpdate={(prevValues, currentValues) =>
              prevValues.type !== currentValues.type
            }
          >
            {({ getFieldValue }) => {
              const serviceType = getFieldValue("type");

              if (serviceType === "WAREHOUSE") {
                return (
                  <>
                    <div className="grid grid-cols-2 gap-4">
                      <Form.Item name="rate" label="Rate ($)">
                        <Input type="number" placeholder="0.00" addonBefore="$" />
                      </Form.Item>
                      <Form.Item name="unit" label="Unit">
                        <Select>
                          <Select.Option value="CU_FT">Cu Foot</Select.Option>
                          <Select.Option value="HOURLY">Hourly</Select.Option>
                          <Select.Option value="EACH">Each</Select.Option>
                        </Select>
                      </Form.Item>
                    </div>
                    <Form.Item name="minimumFee" label="Minimum Fee ($)">
                      <Input type="number" placeholder="0.00" addonBefore="$" />
                    </Form.Item>
                  </>
                );
              }

              if (serviceType === "FABRICATION") {
                return (
                  <>
                    <div className="grid grid-cols-2 gap-4">
                      <Form.Item name="fabricationRateType" label="Rate Type">
                        <Select>
                          <Select.Option value="SQ_FT">
                            Per Square Foot
                          </Select.Option>
                          <Select.Option value="HOUR">Per Hour</Select.Option>
                        </Select>
                      </Form.Item>
                      <Form.Item name="rate" label="Rate ($)">
                        <Input type="number" placeholder="0.00" addonBefore="$" />
                      </Form.Item>
                    </div>
                    <Form.Item name="minimumCharge" label="Minimum Charge ($)">
                      <Input type="number" placeholder="0.00" addonBefore="$" />
                    </Form.Item>
                  </>
                );
              }

              return (
                <Form.Item name="description" label="Description">
                  <Input.TextArea
                    placeholder="Enter service description"
                    rows={3}
                  />
                </Form.Item>
              );
            }}
          </Form.Item>

          <Form.Item name="isActive" valuePropName="checked">
            <Switch /> Active
          </Form.Item>
        </Form>
      </Modal>
    </div>
  );
};
